import { RuleGroupType, RuleGroupTypeAny, RuleType, ValueEditor, ValueEditorProps, findPath, getParentPath, useQueryBuilderQuery } from "react-querybuilder";
import { Input, Select } from "@/components/bp/bp-ui";
import { useEnrollmentPurposeOptions, useCountryOptions, useEyeColorOptions, useHairColorOptions, useFeatureTypeOptions, useSexOptions, useEthnicityOptions, usePhoneTypeOptions, useStateOptions, useAddressTypeOptions, useCollectorOptions, useCityOptions } from "../../../Hooks/LookupOptions";
import BiographicalForm from "../forms/BiographicalForm";
import ContactForm from "../forms/ContactForm";
import EmploymentForm from "../forms/EmploymentForm";
import IdentificationForm from "../forms/IdentificationForm";
import { AlleleTableForm } from "../AlleleTableForm";
import { SampleCreateKeyboardModel } from "@app/api2/api";
import React from "react";

export function EnrollmentPurposeValueEditor(props: ValueEditorProps) {
    const { value, handleOnChange } = props;
    const enrollmentTypes = useEnrollmentPurposeOptions({ defaultValue: value });
    return <Select value={value} search {...enrollmentTypes.getDropdownProps({ onChange: (event, { value }) => handleOnChange(value) })} />;
}

export function NationalityValueEditor(props: ValueEditorProps) {
    const { value, handleOnChange } = props;
    const nationalityOptions = useCountryOptions({ defaultValue: value });
    return <Select value={value} search {...nationalityOptions.getDropdownProps({ onChange: (event, { value }) => handleOnChange(value) })} />;
}

export function EyeColorValueEditor(props: ValueEditorProps) {
    const { value, handleOnChange } = props;
    const eyeColorOptions = useEyeColorOptions({ defaultValue: value });
    return <Select value={value} search {...eyeColorOptions.getDropdownProps({ onChange: (event, { value }) => handleOnChange(value) })} />;
}


export function HairColorValueEditor(props: ValueEditorProps) {
    const { value, handleOnChange } = props;
    const hairColorOptions = useHairColorOptions({ defaultValue: value });
    return <Select value={value} search {...hairColorOptions.getDropdownProps({ onChange: (event, { value }) => handleOnChange(value) })} />;
}

export function FeatureTypeValueEditor(props: ValueEditorProps) {
    const { value, handleOnChange } = props;
    const featureTypeOptions = useFeatureTypeOptions({ defaultValue: value });
    return <Select value={value} search {...featureTypeOptions.getDropdownProps({ onChange: (event, { value }) => handleOnChange(value) })} />;
}

export function AddressTypeValueEditor(props: ValueEditorProps) {
    const { value, handleOnChange } = props;
    const addressTypeOptions = useAddressTypeOptions({ defaultValue: value });
    return <Select value={value} search {...addressTypeOptions.getDropdownProps({ onChange: (event, { value }) => handleOnChange(value) })} />;
}

export function SexValueEditor(props: ValueEditorProps) {
    const { value, handleOnChange } = props;
    const sexOptions = useSexOptions({ defaultValue: value });
    return <Select value={value} search {...sexOptions.getDropdownProps({ onChange: (event, { value }) => handleOnChange(value) })} />;
}


export function EthnicityValueEditor(props: ValueEditorProps) {
    const { value, handleOnChange } = props;
    const ethnicityOptions = useEthnicityOptions({ defaultValue: value });
    return <Select value={value} search {...ethnicityOptions.getDropdownProps({ onChange: (event, { value }) => handleOnChange(value) })} />;
}


export function PhoneTypeValueEditor(props: ValueEditorProps) {
    const { value, handleOnChange } = props;
    const phoneTypeOptions = usePhoneTypeOptions({ defaultValue: value });
    return <Select value={value} search {...phoneTypeOptions.getDropdownProps({ onChange: (event, { value }) => handleOnChange(value) })} />;
}

export function CountryValueEditor(props: ValueEditorProps) {
    const { value, handleOnChange } = props;
    const countryOptions = useCountryOptions({ defaultValue: value });
    return <Select value={value} search {...countryOptions.getDropdownProps({ onChange: (event, { value }) => handleOnChange(value) })} />;
}

export function StateValueEditor(props: ValueEditorProps) {
    const { value, handleOnChange } = props;
    // Get the full query object
    const query = useQueryBuilderQuery();
    // Get the path of this rule's parent group
    const countryId = getSiblings(query, props).find(s => s.field.toLowerCase().endsWith('countryid'))?.value;

    const stateOptions = useStateOptions({ defaultValue: value, countryId: countryId });
    return <Select value={value} search {...stateOptions.getDropdownProps({ onChange: (event, { value }) => handleOnChange(value) })} />;
}

export function CityValueEditor(props: ValueEditorProps) {
    const { value, handleOnChange } = props;
    const [nameStartsWith, setNameStartsWith] = React.useState<string | undefined>(undefined);
    // Get the full query object
    const query = useQueryBuilderQuery();
    // Get the path of this rule's parent group
    const countryId = getSiblings(query, props).find(s => s.field.toLowerCase().endsWith('countryid'))?.value;
    const stateId = getSiblings(query, props).find(s => s.field.toLowerCase().endsWith('stateid'))?.value;
    
    const cityOptions = useCityOptions({ defaultValue: value, stateId: stateId, countryId: countryId, nameStartsWith: nameStartsWith });
    return <Select value={value} limit={0} search onSearchChange={(ev, data) => {
        // console.log('CityValueEditor: onSearchChange:', data.searchQuery);
        setNameStartsWith(data.searchQuery);
        // cityOptions.refresh();
    }} {...cityOptions.getDropdownProps({ onChange: (event, { value }) => handleOnChange(value) })} />;
}

function getSiblings(query: RuleGroupTypeAny, props: ValueEditorProps): RuleType[] {
    const parentPath = getParentPath(props.path);
    // Find the parent group object in the query
    const parentGroup = findPath(parentPath, query) as RuleGroupType;
    const id = findPath(props.path, query)!.id;
    // Get a comma-separated list of all sibling rule values
    const siblingValues = (
        parentGroup.rules.filter(
            r =>
                // filter out groups
                !('rules' in r) &&
                // filter out self
                r.id !== id
        ) as RuleType[]
    );
    return siblingValues;
}

export function BiographicalValueEditor(props: ValueEditorProps) {
    const { value, handleOnChange } = props;
    return <BiographicalForm currentParams={value} setCurrentParams={handleOnChange} onChange={handleOnChange} />;
}

export function ContactValueEditor(props: ValueEditorProps) {
    const { value, handleOnChange } = props;
    return <ContactForm currentParams={value} setCurrentParams={handleOnChange} onChange={handleOnChange} />;
}


export function IdentificationValueEditor(props: ValueEditorProps) {
    const { value, handleOnChange } = props;
    return <IdentificationForm currentParams={value} setCurrentParams={handleOnChange} onChange={handleOnChange} />;
}

export function EmploymentValueEditor(props: ValueEditorProps) {
    const { value, handleOnChange } = props;
    return <EmploymentForm currentParams={value} setCurrentParams={handleOnChange} onChange={handleOnChange} />;
}

export function KeyboardSampleValueEditor(props: ValueEditorProps) {
    const { value, handleOnChange } = props;
    // console.log('KeyboardSampleValueEditor props:', props.value);
    const v = value as SampleCreateKeyboardModel;
    return <AlleleTableForm value={v}
        onChange={handleOnChange} />;
}

export function CollectorValueEditor(props: ValueEditorProps) {
    const { value, handleOnChange } = props;
    const collectors = useCollectorOptions({ selectedId: parseInt(value) });

    return <Select {...collectors.getDropdownProps({ onChange: (event, { value }) => handleOnChange(value) })} />;
};

export const CustomValueEditor = (props: ValueEditorProps) => {
    // console.log('CustomValueEditor props:', props);
    if (props.fieldData.name === 'biographical') {
        return <BiographicalValueEditor {...props} />;
    } else if (props.fieldData.name === 'contact') {
        return <ContactValueEditor {...props} />;
    } else if (props.fieldData.name === 'identification') {
        return <IdentificationValueEditor {...props} />;
    } else if (props.fieldData.name === 'employment') {
        return <EmploymentValueEditor {...props} />;
    } else if (props.fieldData.name === 'keyboardSample') {
        return <KeyboardSampleValueEditor {...props} />;
    } else if (props.fieldData.name === 'enrollmentPurposeId') {
        return <EnrollmentPurposeValueEditor {...props} />;
    } else if (props.fieldData.name === 'nationalityId') {
        return <NationalityValueEditor {...props} />;
    } else if (props.fieldData.name === 'eyeColorId') {
        return <EyeColorValueEditor {...props} />;
    } else if (props.fieldData.name === 'hairColorId') {
        return <HairColorValueEditor {...props} />;
    } else if (props.fieldData.name === 'featureTypeId') {
        return <FeatureTypeValueEditor {...props} />;
    } else if (props.fieldData.name === 'sexId') {
        return <SexValueEditor {...props} />;
    } else if (props.fieldData.name === 'ethnicityId') {
        return <EthnicityValueEditor {...props} />;
    } else if (props.fieldData.name === 'phoneTypeId') {
        return <PhoneTypeValueEditor {...props} />;
    } else if (props.fieldData.name.toLowerCase().endsWith('countryid')) {
        return <CountryValueEditor {...props} />;
    } else if (props.fieldData.name.toLowerCase().endsWith('stateid')) {
        return <StateValueEditor {...props} />;
    } else if (props.fieldData.name.toLowerCase().endsWith('cityid')) {
        return <CityValueEditor {...props} />;
    } else if (props.fieldData.name.toLowerCase().endsWith('addresstypeid')) {
        return <AddressTypeValueEditor {...props} />;
    } else if (props.fieldData.name.toLowerCase().endsWith('collectorid')) {
        return <CollectorValueEditor {...props} />;
    } 
    
    
    else if (props.fieldData.inputType === 'string') {
        return (
            <Input
                value={props.value}
                onChange={(e) => props.handleOnChange(e.target.value)}
            />
        );
    } else if (props.fieldData.datatype === 'date') {
        return (
            <Input
                type='date'
                value={props.value}
                onChange={(e) => props.handleOnChange(e.target.value)}
            />
            // <ReactDatePicker
            //     dateFormat={dateFormat}
            //     selected={!props.value ? new Date() : parse(props.value, dateFormat, new Date())}
            //     onChange={(d: Date) => {
            //         return props.handleOnChange(d ? format(d, dateFormat) : null);
            //     }}
            // />
        );
    }


    return <ValueEditor {...props} />;
};

